import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import LeadCard from "@/components/LeadCard";
import { api } from "@/lib/api";

const BUDGETS = { any: [null, null], lt50: [null, 50000], "50-200": [50000, 200000], "200-500": [200000, 500000], gt500: [500000, null] };
const INIT = { search: "", category: "all", country: "all", industry: "all", budget: "any", from: null, to: null };

const FilterSelect = ({ value, onChange, placeholder, options, testId, allLabel }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Marketplace() {
  const [f, setF] = useState(INIT);
  const [opts, setOpts] = useState({ categories: [], countries: [], industries: [] });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); };

  useEffect(() => {
    api.get("/leads/filters").then((r) => setOpts(r.data));
  }, []);

  useEffect(() => {
    const [bmin, bmax] = BUDGETS[f.budget];
    const params = { page, page_size: 12, search: f.search };
    ["category", "country", "industry"].forEach((k) => { if (f[k] !== "all") params[k] = f[k]; });
    if (bmin != null) params.budget_min = bmin;
    if (bmax != null) params.budget_max = bmax;
    if (f.from) params.date_from = format(f.from, "yyyy-MM-dd");
    if (f.to) params.date_to = format(f.to, "yyyy-MM-dd");
    const t = setTimeout(() => api.get("/leads", { params }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [f, page]);

  return (
    <div>
      <PageHeader eyebrow="Marketplace" title="Find your next client" subtitle="Every listing is manually verified. Contact details unlock with credits or an active plan." />
      <div className="bg-card rounded-xl border p-4 sm:p-5 mb-6 fade-up">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="marketplace-search-input" placeholder="Search by project title or requirement..." className="pl-9 h-11" value={f.search} onChange={(e) => set("search")(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-3">
          <FilterSelect value={f.category} onChange={set("category")} options={opts.categories} allLabel="All categories" testId="filter-category-select" />
          <FilterSelect value={f.country} onChange={set("country")} options={opts.countries} allLabel="All countries" testId="filter-country-select" />
          <FilterSelect value={f.industry} onChange={set("industry")} options={opts.industries} allLabel="All industries" testId="filter-industry-select" />
          <Select value={f.budget} onValueChange={set("budget")}>
            <SelectTrigger data-testid="filter-budget-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any budget</SelectItem><SelectItem value="lt50">Under ₹50K</SelectItem><SelectItem value="50-200">₹50K – ₹2L</SelectItem>
              <SelectItem value="200-500">₹2L – ₹5L</SelectItem><SelectItem value="gt500">₹5L+</SelectItem>
            </SelectContent>
          </Select>
          <DatePicker value={f.from} onChange={set("from")} placeholder="From date" testId="filter-date-from" />
          <DatePicker value={f.to} onChange={set("to")} placeholder="To date" testId="filter-date-to" />
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5" data-testid="marketplace-result-count"><SlidersHorizontal className="h-3.5 w-3.5" />{data ? `${data.total} leads found` : "Loading..."}</span>
          <button className="hover:text-foreground" data-testid="filters-reset-button" onClick={() => { setF(INIT); setPage(1); }}>Reset filters</button>
        </div>
      </div>
      {data && data.items.length === 0 ? (
        <EmptyState title="No leads match your filters" text="Try widening your search." testId="marketplace-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="marketplace-grid">
          {(data?.items || []).map((l, i) => <LeadCard key={l.id} lead={l} delay={i * 40} />)}
        </div>
      )}
      {data?.pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-mono">{page} / {data.pages}</span>
          <Button variant="outline" size="icon" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
